// =============================================================================
// Chave de permissão exigida por cada rota autenticada.
//
// É o espelho, no front, do que o backend exige nas rotas que cada tela consome
// (`requirePermission` em `routes.js` do repositório irmão; mapa em `contexto.md`
// §5.5, linha "RBAC"). Serve só para NÃO desenhar a tela a quem o cargo não
// permite — o portão de verdade continua sendo o servidor.
//
// - `perm` é uma chave ou uma lista ("qualquer uma libera"), como no menu lateral.
// - `:param` casa um segmento qualquer da URL.
// - Rota que não está aqui fica livre (Visão Geral, perfil, suporte, mensagens,
//   área da plataforma `/admin/*`…).
// =============================================================================

import { hasAnyPermission } from "./permissions";

export const ROUTE_ACCESS = [
  // CRM
  { path: "/board", perm: "crm.funnel.view" },
  { path: "/crm", perm: "crm.funnel.view" },
  { path: "/funnels", perm: "crm.funnel.manage" },
  { path: "/crm/tasks", perm: "crm.task.view" },
  { path: "/crm/automations", perm: "crm.automation.view" },
  { path: "/crm/fields", perm: "crm.customfield.manage" },
  { path: "/crm/public-forms/:funnelId", perm: "crm.publicform.manage" },
  { path: "/crm/funnels/:funnelId/public-form", perm: "crm.publicform.manage" },
  {
    path: "/crm/funnels/:funnelId/card-public-forms",
    perm: "crm.publicform.manage",
  },
  { path: "/crm/stages/:stageId/form", perm: "crm.funnel.manage" },

  // Agenda
  { path: "/calendario", perm: "calendar.view" },
  { path: "/agendamentos", perm: "calendar.booking.manage" },

  // Contatos
  { path: "/contacts", perm: "contacts.lead.view" },
  { path: "/contacts/pontuacao", perm: "scoring.view" },
  { path: "/contacts/custom-fields", perm: "crm.customfield.manage" },
  { path: "/contacts/sources", perm: "contacts.source.manage" },
  { path: "/contacts/tags", perm: "crm.tag.manage" },
  { path: "/leads/import", perm: "contacts.lead.import" },
  { path: "/leads/generator", perm: "contacts.generator.use" },
  { path: "/cadencias", perm: "cadence.view" },
  { path: "/cadencias/:cadenceId", perm: "cadence.view" },

  // Catálogo e orçamentos
  { path: "/products", perm: "catalog.product.view" },
  { path: "/products/import", perm: "catalog.product.manage" },
  { path: "/products/units", perm: "catalog.product.manage" },
  { path: "/budgets", perm: "catalog.budget.view" },
  // A mesma tela cria (create), edita (manage) e só mostra um orçamento já feito
  // (view); o que cada botão faz dentro dela segue a chave da ação.
  { path: "/budget", perm: "catalog.budget.view" },

  // Desempenho
  { path: "/reports", perm: "reports.view" },
  { path: "/reports/metrics/new", perm: "reports.manage" },
  { path: "/desempenho/escritorio", perm: "reports.view" },
  { path: "/desempenho/ligacoes", perm: "crm.card.view" },
  { path: "/metas", perm: "goals.view" },
  { path: "/metas/nova", perm: "goals.manage" },
  { path: "/insights", perm: "ai.insights.view" },

  // Gestão
  { path: "/empresa", perm: "settings.company.manage" },
  { path: "/teams", perm: "settings.team.manage" },
  { path: "/users", perm: ["settings.user.manage", "settings.role.manage"] },
  { path: "/desenvolvedor", perm: ["api.key.manage", "api.webhook.manage"] },
  { path: "/integracoes", perm: "settings.integration.manage" },
  { path: "/integracoes/meta-ads", perm: "settings.integration.manage" },
  { path: "/assinatura", perm: "settings.billing.manage" },
  { path: "/assinatura/pagamento", perm: "settings.billing.manage" },

  // Comunicação
  { path: "/whatsapp", perm: "whatsapp.chat.access" },
  { path: "/whatsapp/web", perm: "whatsapp.chat.access" },
  { path: "/whatsapp/flows", perm: "whatsapp.flow.manage" },
  { path: "/whatsapp/flows/:flowId", perm: "whatsapp.flow.manage" },
  { path: "/whatsapp/ai-agent", perm: "whatsapp.ai.manage" },
  { path: "/chat", perm: "chat.internal.access" },
];

const splitPath = (path) =>
  String(path || "")
    .split("/")
    .filter(Boolean);

const matchesPattern = (pattern, pathname) => {
  const a = splitPath(pattern);
  const b = splitPath(pathname);
  if (a.length !== b.length) return false;
  return a.every((segment, index) => segment.startsWith(":") || segment === b[index]);
};

/** Chave (ou lista de chaves) que a rota exige; `null` quando a rota é livre. */
export const getRoutePermission = (pathname) => {
  const found = ROUTE_ACCESS.find((route) => matchesPattern(route.path, pathname));
  return found ? found.perm : null;
};

/**
 * `true` quando o usuário pode abrir a rota. Enquanto `/me/access` não chegou
 * (`access` indefinido) vale "permitido", como no resto do app: o servidor é
 * quem recusa de verdade.
 */
export const canAccessRoute = (access, pathname) => {
  const perm = getRoutePermission(pathname);
  if (!perm) return true;
  return hasAnyPermission(access, perm);
};
